'use client';

import { useId, useRef } from 'react';
import { IconButton, Menu, MetaDefault } from '@terra/ui-design-system';
import { Portal } from '@ark-ui/react';
import { Box, HStack } from '@terra/ui-styled-system';
import {
  ArrowRight01Icon,
  CheckListIcon,
  AttachmentIcon,
  AtIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { SESSION_ATTACHMENT_ACCEPT } from '@terra/shared-contracts-conversation-attachments';
import type { MentionsMenuComposerRef } from '../mentions/MentionsMenuComposer';

type ComposerAttachMenuProps = {
  isLoading?: boolean;
  mentionsMenuRef?: React.RefObject<MentionsMenuComposerRef | null>;
  onFilesSelected: (files: File[]) => void;
};

export function ComposerAttachMenu({
  isLoading,
  mentionsMenuRef,
  onFilesSelected,
}: ComposerAttachMenuProps) {
  const tCopilot = useTranslations('chat.copilot');
  const tFileUpload = useTranslations('designSystem.chat.fileUpload');
  const openMenuLabel = tCopilot('openAttachMenuLabel');
  const triggerRectRef = useRef<DOMRect | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileInputId = useId();

  const openMentionsMenu = (scope: 'objects' | 'testingInstruction') => {
    const rect = triggerRectRef.current;

    window.setTimeout(() => {
      mentionsMenuRef?.current?.open({
        scope,
        ...(rect
          ? {
              x: rect.left,
              y: rect.top,
            }
          : {}),
      });
    }, 0);
  };

  const items = [
    {
      value: 'upload-files',
      disabled: false,
      onClick: () => {
        fileInputRef.current?.click();
      },
      label: (
        <label
          htmlFor={fileInputId}
          style={{
            alignItems: 'center',
            cursor: 'pointer',
            display: 'flex',
            gap: '8px',
            width: '100%',
          }}
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <AttachmentIcon size={14} />
          <MetaDefault>{tFileUpload('menuTitle')}</MetaDefault>
        </label>
      ),
    },
    {
      value: 'mention-objects',
      disabled: !mentionsMenuRef,
      onClick: () => {
        openMentionsMenu('objects');
      },
      label: (
        <HStack gap={2} alignItems="center" justify="space-between" w="full">
          <HStack gap={2} alignItems="center">
            <AtIcon size={14} />
            <MetaDefault>{tCopilot('mentionObjects')}</MetaDefault>
          </HStack>
          <Box color="fg.muted" display="flex" alignItems="center">
            <ArrowRight01Icon size={14} />
          </Box>
        </HStack>
      ),
    },
    {
      value: 'insert-testing-instruction',
      disabled: !mentionsMenuRef,
      onClick: () => {
        openMentionsMenu('testingInstruction');
      },
      label: (
        <HStack gap={2} alignItems="center" justify="space-between" w="full">
          <HStack gap={2} alignItems="center">
            <CheckListIcon size={14} />
            <MetaDefault>{tCopilot('insertTestingInstruction')}</MetaDefault>
          </HStack>
          <Box color="fg.muted" display="flex" alignItems="center">
            <ArrowRight01Icon size={14} />
          </Box>
        </HStack>
      ),
    },
  ];

  return (
    <Box position="relative" display="inline-flex">
      <input
        id={fileInputId}
        ref={fileInputRef}
        type="file"
        multiple
        accept={SESSION_ATTACHMENT_ACCEPT}
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);

          event.target.value = '';
          if (files.length > 0) {
            onFilesSelected(files);
          }
        }}
      />
      <Menu.Root
        lazyMount
        unmountOnExit
        positioning={{ strategy: 'fixed', placement: 'top-end' }}
      >
        <Menu.Trigger asChild>
          <IconButton
            type="button"
            variant="ghost"
            aria-label={openMenuLabel}
            disabled={isLoading}
            _hover={{
              color: 'fg.default',
              bg: 'transparent',
            }}
            _disabled={{
              color: 'fg.subtle',
              cursor: 'not-allowed',
            }}
            size="xs"
            onPointerDown={(event) => {
              triggerRectRef.current =
                event.currentTarget.getBoundingClientRect();
            }}
          >
            <AttachmentIcon size={16} />
          </IconButton>
        </Menu.Trigger>
        <Portal>
          <Menu.Positioner>
            <Menu.Content p="1" w="220px">
              {items.map((item) => (
                <Menu.Item
                  key={item.value}
                  value={item.value}
                  disabled={item.disabled}
                  onClick={item.disabled ? undefined : item.onClick}
                  py="1"
                  px="2"
                >
                  {item.label}
                </Menu.Item>
              ))}
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
    </Box>
  );
}
